import React, { useEffect, useState, useRef } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { TelegramProvider } from "./TelegramProvider";
import { MaxProvider } from "./MaxProvider";
import { RuntimeMessengerContext } from "./RuntimeMessengerContext";
import { AppDialogProvider } from "./AppDialogProvider";
import { resolveRuntimeMessenger } from "@/shared/utils/runtimeMessenger";
import type { MessengerKind } from "@/shared/utils/runtimeMessenger";

interface AppProvidersProps {
  children: React.ReactNode;
}

const SCRIPT_URLS: Record<Exclude<MessengerKind, "web">, string> = {
  telegram: "https://telegram.org/js/telegram-web-app.js",
  max: "https://st.max.ru/js/max-web-app.js",
};

const POLL_MS = 200;
const POLL_DURATION_MS = 5000;

const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`Не удалось загрузить скрипт: ${src}`));

    document.head.appendChild(script);
  });
};

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  const [runtime, setRuntime] = useState(() => resolveRuntimeMessenger());
  const [isReady, setIsReady] = useState(false);
  const initRef = useRef(false);

  const messengerType = runtime.kind;

  useEffect(() => {
    const tick = () => setRuntime(resolveRuntimeMessenger());
    tick();
    const interval = window.setInterval(tick, POLL_MS);
    const stopPoll = window.setTimeout(() => {
      window.clearInterval(interval);
      tick();
    }, POLL_DURATION_MS);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(stopPoll);
    };
  }, []);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    queueMicrotask(() => {
      const start = resolveRuntimeMessenger();
      setRuntime(start);

      if (start.kind === "web") {
        setIsReady(true);
        return;
      }

      loadScript(SCRIPT_URLS[start.kind])
        .then(() => {
          setRuntime(resolveRuntimeMessenger());
        })
        .catch((error) => {
          console.error(
            `[AppProviders] Ошибка при загрузке скрипта для ${start.kind}:`,
            error
          );
        })
        .finally(() => {
          setRuntime(resolveRuntimeMessenger());
          setIsReady(true);
        });
    });
  }, []);

  if (!isReady) {
    return null;
  }

  const inner =
    messengerType === "telegram" ? (
      <TelegramProvider>{children}</TelegramProvider>
    ) : messengerType === "max" ? (
      <MaxProvider>{children}</MaxProvider>
    ) : (
      children
    );

  return (
    <RuntimeMessengerContext.Provider value={runtime}>
      <CssBaseline />
      <AppDialogProvider>{inner}</AppDialogProvider>
    </RuntimeMessengerContext.Provider>
  );
};
